import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { cn } from '@/app/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogOriginalClose = DialogPrimitive.Close;

/**
 * 获取或创建全局 dark 主题容器
 * 用于让 useGlobalModal 的公共弹窗默认不受 claw light 模式影响
 */
let globalDarkContainer: HTMLDivElement | null = null;

const getGlobalDarkContainer = (): HTMLDivElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  if (!globalDarkContainer) {
    globalDarkContainer = document.createElement('div');
    globalDarkContainer.className = 'dark';
    globalDarkContainer.setAttribute('data-theme', 'dark');
    document.body.appendChild(globalDarkContainer);
  }

  return globalDarkContainer;
};

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> & {
    closeIcon?: React.ReactNode;
  }
>(({ className, closeIcon, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    className={cn(
      'absolute top-0 right-0 flex size-7 items-center justify-center text-xl disabled:pointer-events-none',
      className,
    )}
    {...props}
  >
    {closeIcon || <BaseLineClose />}
    <span className="sr-only">Close</span>
  </DialogPrimitive.Close>
));
DialogClose.displayName = 'DialogClose';

const DialogOriginalContent = DialogPrimitive.Content;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/70 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    container?: HTMLElement | null;
    overlayClassname?: string;
    overlayStyle?: React.CSSProperties;
    closeClassName?: string;
    closable?: boolean;
    onClose?: () => void;
    /**
     * 'default': 居中展示
     * 'fullScreen': 全屏
     * 'mobileFullScreen': pc 居中，mobile 全屏
     */
    variant?: 'default' | 'fullScreen' | 'mobileFullScreen';
    closeIcon?: React.ReactNode;
    /**
     * 是否禁用点击蒙层关闭
     * 默认为 false，允许点击蒙层关闭
     */
    disableOverlayClose?: boolean;
    /**
     * 是否继承全局主题（如 claw light 模式）
     * 默认 false：portal 挂到 dark 容器，不受 light 主题影响
     * true：portal 挂到传入的 container 或默认位置，跟随全局主题
     */
    inheritTheme?: boolean;
  }
>(
  (
    {
      className,
      children,
      container,
      overlayClassname,
      overlayStyle,
      closeClassName,
      closable = true,
      onClose,
      variant = 'default',
      closeIcon,
      disableOverlayClose = false,
      inheritTheme = false,
      onPointerDownOutside,
      onInteractOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => {
    const portalContainer = inheritTheme ? container : (container ?? getGlobalDarkContainer());
    return (
      <DialogPortal container={portalContainer}>
        <DialogOverlay className={overlayClassname} style={overlayStyle} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed z-50 mx-auto grid gap-4 overflow-hidden bg-system-bg04 p-6 duration-200 sm:gap-4 md:p-4',
            variant === 'fullScreen'
              ? 'top-0 left-0 h-screen w-full min-w-full max-w-full overflow-y-auto sm:pb-[var(--ios-safe-bottom)]'
              : 'top-[50%] left-[50%] max-h-[calc(100dvh-32px)] w-[90%] max-w-2xl translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-12 border border-[#FFFFFF1F] [box-shadow:0px_20px_60px_0px_rgba(0,0,0,0.32)] sm:max-w-[calc(100%-32px)]',
            variant === 'mobileFullScreen' &&
              'sm:top-0 sm:left-0 sm:h-screen sm:w-full sm:min-w-full sm:max-w-full sm:translate-x-0 sm:translate-y-0 sm:overflow-y-auto sm:rounded-none sm:border-none sm:pb-[var(--ios-safe-bottom)] sm:shadow-none',
            className,
          )}
          onPointerDownOutside={(e) => {
            if (disableOverlayClose) {
              e.preventDefault();
            }
            onPointerDownOutside?.(e);
          }}
          onInteractOutside={(e) => {
            if (disableOverlayClose) {
              e.preventDefault();
            }
            onInteractOutside?.(e);
          }}
          onEscapeKeyDown={(e) => {
            if (disableOverlayClose) {
              e.preventDefault();
            }
            onEscapeKeyDown?.(e);
          }}
          {...props}
        >
          {/* 如果不加 title 和 description 会一直报错。。。 */}
          <DialogPrimitive.Title className="hidden" />
          <DialogPrimitive.Description className="hidden" />
          {children}
          {closable && (
            <DialogClose
              className={cn('top-6 right-6 md:top-4 md:right-4', closeClassName)}
              onClick={onClose}
              closeIcon={closeIcon}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-4 sm:gap-4', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex justify-end space-x-4', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('font-medium text-system-text01 text-xl', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-system-white64', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogOriginalContent,
  DialogOriginalClose,
};
